import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Loader2 } from "lucide-react";
import { OrganizationWithRelations } from "@/types/organization.types";

interface DeleteOrganizationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  organization: OrganizationWithRelations;
  onDelete: (id: string) => Promise<void>;
}

export function DeleteOrganizationDialog({
  isOpen,
  onClose,
  organization,
  onDelete,
}: DeleteOrganizationDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      setError(null);
      await onDelete(organization.id);
      navigate("/organizations");
    } catch (err: any) {
      setError(err.message || "An error occurred while deleting the organization");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {organization.name}</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this organization? This action cannot be undone.
            {error && (
              <div className="mt-2 rounded-md bg-destructive/10 p-3 text-destructive">
                {error}
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}